import {
  DocumentAnswerState,
  DocumentSectionSharedUserStatus,
  DocumentSectionSharedUserType,
  DocumentSectionType
} from "constants/documents";

import { ChartType, CurrencyType, DateRangeValue, DealBase, IAxis, ReportFilters } from "..";
import { IFile, IUpdateShareState } from "../deal";
import { UserLight } from "../user";

export type Document<WithContent extends boolean = false> = WithContent extends true
  ? DocumentBase & DocumentContent
  : DocumentBase;

export interface DocumentBase {
  id: number;
  account_id: number;
  deal_id: number | null;
  deal_title: string | null;
  user_id: number | null;
  title: string;
  section_id: number | null; // section id of folder
  section_title: string | null;
  document_ui_id: number; // This specific field only for UI, in case to understand which element is document in united list in databox and checkbox in share. The value is equal to primary key
  is_private: boolean;

  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;

  files: IFile[]; //need in case to show attached files in global document "workspace", because in deal workspace we call GET files and them merge it with docs

  report_data: {
    queries?: DocumentReportQueries;
  } | null;
}

export type DocumentReportQueries = {
  dateRange: DateRangeValue;
  currency: CurrencyType;
  filters: ReportFilters;
  exchangeMethod: "toDate" | "realDate";
};

export interface DocumentContent {
  sections: DocumentSection[];
  questions: DocumentQuestion[];
  answers: DocumentAnswer[];
  report_data: {
    queries?: DocumentReportQueries;
  } | null;
}

export interface DocumentQuestion {
  id: number;
  section_id: number;
  document_id: number;
  question: string;
  extra_data: string;
  has_ai_answers?: boolean;
  created_at: string;
  updated_at: string;
}
interface IAiFile {
  id: number;
  upload_type: string;
  name: string;
}

export type DocumentSection =
  | DocumentSectionText
  | DocumentSectionQuestion
  | DocumentSectionAIQuestion
  | DocumentSectionFile
  | DocumentSectionImage
  | DocumentSectionAIFile
  | DocumentSectionRichText
  | DocumentSectionKPI
  | DocumentSectionGraph
  | DocumentSectionTable
  | DocumentSectionCashflowGraph
  | DocumentSectionMeetingNotes
  | DocumentSectionWebSearch
  | DocumentSectionAnalyzeFundTable;

export interface DocumentSectionBase {
  id: number;
  account_id: number;
  document_id: number;
  type: DocumentSectionType;
  text: string | null;
  order: number;
  are_comments_shared: boolean;
  is_rtl: boolean;

  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  ai_files?: IAiFile[] | null;
  ai_status?: null | QuestionAiStatus;
  title?: string;

  section_shared_users: DocumentSectionSharedUser[];
  parameters: {
    height?: number;
    width?: number;
    alignment?: "left" | "center" | "right";
    question?: string;
  } | null;
}

export enum QuestionAiStatus {
  Null = "null",
  InProgress = "in-progress",
  Finished = "finished",
  Failed = "failed"
}

// export interface DocumentSectionGraphData {
//   id: number;
//   settings: {
//     measure: string;
//     group_by: string;
//     chart_type: ChartType;
//   };
//   data: {
//     title: string;
//     series: Array<{
//       name: string;
//       y: number;
//     }>;
//   };
// }

export interface DocumentSectionText extends DocumentSectionBase {
  type: DocumentSectionType.Text;
}

export interface DocumentSectionQuestion extends DocumentSectionBase {
  type: DocumentSectionType.Question;
}
export interface DocumentSectionAIQuestion extends DocumentSectionBase {
  type: DocumentSectionType.AIQuestion;
}

export interface DocumentSectionFile extends DocumentSectionBase {
  type: DocumentSectionType.File;
  file: IFile | null;
}

export interface DocumentSectionImage extends DocumentSectionBase {
  type: DocumentSectionType.Image;
  file?: IFile | null;
}

export interface DocumentSectionAIFile extends DocumentSectionBase {
  type: DocumentSectionType.AIFile;
  file: IFile;
}

export interface DocumentSectionRichText extends DocumentSectionBase {
  type: DocumentSectionType.RichText;
}

export interface DocumentSectionWebSearch extends DocumentSectionBase {
  type: DocumentSectionType.WebSearch;
}

export interface DocumentSectionKPI extends DocumentSectionBase {
  type: DocumentSectionType.KPI;
  report_data: {
    settings: { filter: string; title: string }[];
    queries?: DocumentReportQueries;
    // data?: string[];
  };
}

export interface DocumentSectionGraph extends DocumentSectionBase {
  type: DocumentSectionType.Graph;
  report_data: {
    settings: { measure: string | null; group_by: string; chart_type: ChartType; xAxis?: IAxis; yAxis?: IAxis }[];
    queries?: DocumentReportQueries;
    // data: { title: string; series: Array<{ name: string; y: number }> }[];
  };
}

export interface DocumentSectionCashflowGraph extends DocumentSectionBase {
  type: DocumentSectionType.CashflowGraph;
  report_data: {
    settings: { set_id: number };
    queries?: DocumentReportQueries;
  };
}

export interface DocumentSectionTable extends DocumentSectionBase {
  type: DocumentSectionType.Table;
  report_data: {
    settings: {
      name: string;
      columns: { calcFields: string[]; characteristics: string[]; measures: string[] };
      index_name: {
        name: string;
        index: string;
      } | null;
      group_by: string[];
      original_currency: boolean;
    };
    // data: ReportTableData;
    queries?: DocumentReportQueries;
  };
}

export interface DocumentSectionAnalyzeFundTable extends DocumentSectionBase {
  type: DocumentSectionType.AnalyzeFundTable;
  file: IFile;
  report_data: {
    columns?: {
      name: string;
      key: string;
    }[];
    rows?: {
      value: number | string | boolean;
      key: string;
      tooltip: string | null;
      currency: CurrencyType | null;
    }[][];
  } | null;
}
export interface DocumentSectionMeetingNotes extends DocumentSectionBase {
  type: DocumentSectionType.Meeting;
  meeting: DocumentMeeting;
}

export interface DocumentMeeting {
  transcription: string | null;
  document_section_id: number;
  document_id: number;
  id: number;
  is_ai: boolean;
  title: string;
  participants: string[];
  meeting_date: string;
  meeting_time_start: string;
  meeting_time_end: string;
  description: string;
}

export interface DocumentAnswer {
  id: number;
  account_id: number;
  user_id: number;
  question_id: number;
  section_id: number;
  parent_answer_id: number | null;
  is_rtl: boolean;
  answer: {
    text: string | null;
  };
  user: UserLight;
  published_at: string | null;
  state: DocumentAnswerState;

  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface DocumentAnswerHistory {
  id: number;
  account_id: number;
  answer_id: number;
  answer: string;

  created_at: string;
  updated_at: string | null;
}

export interface DocumentSectionSharedUser {
  account_id: number;
  section_id: number;
  user_id: number;
  status: DocumentSectionSharedUserStatus;
  type: DocumentSectionSharedUserType;
  user: UserLight;

  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

// ---------------------------------------------- API ----------------------------------------
export interface ICreateDocumentData {
  title: string;
  deal_id?: number;
  section_id?: number;
  parent_section_id?: number;
  section_title?: string;
  disableDispatch?: boolean;
}

export type ICreateDocumentSectionData<T extends DocumentSectionType = DocumentSectionType> = {
  document_id: number;
  text?: string;
  type: T;
  order: number;
  file_id?: number;
  parameters?: {};
} & (T extends DocumentSectionType.Graph | DocumentSectionType.KPI | DocumentSectionType.Table
  ? {
      report_data: {
        settings: DocumentReportsSectionSettings[T];
        filters?: { date_range: DateRangeValue; currency: CurrencyType; parameters: ReportFilters };
      };
    }
  : {});

export type DocumentReportsSectionSettings = {
  [DocumentSectionType.KPI]: DocumentSectionKPI["report_data"]["settings"];
  [DocumentSectionType.Graph]: DocumentSectionGraph["report_data"]["settings"];
  [DocumentSectionType.Table]: DocumentSectionTable["report_data"]["settings"];
  [DocumentSectionType.CashflowGraph]: DocumentSectionCashflowGraph["report_data"]["settings"];
};

export type IUpdateDocumentSectionData = Pick<DocumentSection, "id"> &
  Partial<Pick<DocumentSection, "text" | "type" | "is_rtl" | "parameters" | "title">> & {
    report_data?: {
      settings?:
        | DocumentReportsSectionSettings[DocumentSectionType.KPI]
        | DocumentReportsSectionSettings[DocumentSectionType.Graph]
        | DocumentReportsSectionSettings[DocumentSectionType.Table]
        | DocumentReportsSectionSettings[DocumentSectionType.CashflowGraph];
      filters?: { date_range: DateRangeValue; currency: CurrencyType; parameters: ReportFilters };
    };
  };

export interface ICreateMeetingSectionData {
  title: string;
  meeting_date: string;
  meeting_time_start: string;
  meeting_time_end: string;
  participants: string[];
  document_id: number;
  document_section_order: number;
}

export interface IUpdateMeetingSectionData {
  title?: string;
  meeting_date?: string;
  meeting_time_start?: string;
  meeting_time_end?: string;
  participants?: string[];
  description?: string;
}

export interface ICreateDocumentQuestion {
  question?: string;
  section_id: number;
  documentId: number;
}

export interface IUpdateDocumentQuestionData {
  documentId: number;
  id: DocumentQuestion["id"];
  question: DocumentQuestion["question"];
}

export type UpdateDocumentAnswerData = {
  id: DocumentAnswer["id"];
  documentId: number;
} & Partial<{
  state: DocumentAnswer["state"];
  is_rtl?: boolean;
  is_published: boolean;
  answer: {
    text: string;
  };
}>;

export interface DocumentSectionSharedUser {
  account_id: number;
  section_id: number;
  user_id: number;
  status: DocumentSectionSharedUserStatus;
  type: DocumentSectionSharedUserType;
  user: UserLight;

  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export type IDocumentType = "all_documents" | "general" | "deals";

export interface IGetGlobalDocumentsQuery {
  type?: IDocumentType;
  page?: number;
  per_page?: number;
  search?: string;
  sort?: "recent" | "alphabetical";
  replaceDocuments?: boolean;
  id?: number; // documentId from email or notification
}

export interface IGetGlobalDocumentsResponse {
  documents: Document[];
  deals: DealBase[];
  total: number;
  last_page: number;
  per_page: number;
}

// ---------------------------------------------- API ----------------------------------------

export type ShareDocumentSectionsData = {
  documentId: number;
  sections: number[];
  users: number[];
  type: "viewer" | "guest";
  message: string;
};

export type DocumentUploadType = Extract<IFile["upload_type"], "document-image" | "document-file">;

export interface IUploadGlobalDocumentFileData {
  file: File;
  upload_type: DocumentUploadType;
}

export interface IMoveDocumentData {
  documentId: number;
  deal_id: number;
  section_id?: number;
  parent_section_id?: number;
  section_title?: string;
}

export interface ICreateGlobalDocumentShareData {
  emails: string[];
  role_id: number;
  message?: string;
  document_id: number[];
}

export interface IUpdateGlobalDocumentShareData extends IUpdateShareState {
  document_id: number;
}

export type IAskAiQueryType = "web_search" | "file_query" | "ai_questions";
export interface ICreateDocumentAIAnswerPayload {
  file_ids?: number[];
  files?: File[];
  documentId: number;
  send_ai: boolean;
  sectionId: number;
  query_type?: IAskAiQueryType;
  user_prompt?: string;
}
